import { useState } from "react";
import { BadgePercent, Loader2, X } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const DURATIONS = [["once", "Next invoice only"], ["repeating", "For N months"], ["forever", "Every invoice"]];

export const DiscountPanel = ({ company, busy, act }) => {
  const [form, setForm] = useState({ kind: "percent", value: "20", duration: "repeating", months: "3", note: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="admin-discount-panel" className="mt-6 rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-4">
      <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-emerald-300">
        <BadgePercent className="h-3.5 w-3.5" /> Discount
      </p>
      {company.discount ? (
        <div className="mt-3">
          <p data-testid="admin-discount-current" className="text-sm text-slate-200">
            {company.discount}
            {company.discount_note ? ` · ${company.discount_note}` : ""}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            This is a real Stripe coupon — it shows on their billing screen and reduces what they pay.
          </p>
          <Button data-testid="admin-discount-remove" size="sm" disabled={busy}
            onClick={() => act(() => api.delete(`/admin/companies/${company.id}/discount`),
              `Discount removed from ${company.name}.`)}
            className="mt-3 bg-red-500/20 text-red-100 hover:bg-red-500/30">
            <X className="mr-2 h-4 w-4" /> Remove discount
          </Button>
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          <p className="text-xs text-slate-400">
            Creates a Stripe coupon and applies it to their live subscription. If they are not
            subscribed yet, it is applied automatically at their next checkout.
          </p>
          <div className="flex flex-wrap gap-2">
            {[["percent", "% off"], ["amount", "$ off"]].map(([k, l]) => (
              <button key={k} data-testid={`admin-discount-kind-${k}`} onClick={() => set("kind", k)}
                className={`rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest ${
                  form.kind === k ? "border-emerald-400/50 bg-emerald-400/10 text-emerald-200"
                    : "border-ink-600 text-slate-400 hover:text-slate-100"}`}>
                {l}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Input data-testid="admin-discount-value" type="number" min="1" value={form.value}
              onChange={(e) => set("value", e.target.value)}
              className="h-11 w-28 border-ink-600 bg-ink-900 text-slate-100" />
            <div className="flex flex-wrap gap-2">
              {DURATIONS.map(([k, l]) => (
                <button key={k} data-testid={`admin-discount-duration-${k}`} onClick={() => set("duration", k)}
                  className={`rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest ${
                    form.duration === k ? "border-emerald-400/50 bg-emerald-400/10 text-emerald-200"
                      : "border-ink-600 text-slate-400 hover:text-slate-100"}`}>
                  {l}
                </button>
              ))}
            </div>
            {form.duration === "repeating" && (
              <Input data-testid="admin-discount-months" type="number" min="1" max="36" value={form.months}
                onChange={(e) => set("months", e.target.value)}
                className="h-11 w-24 border-ink-600 bg-ink-900 text-slate-100" />
            )}
          </div>
          <Input data-testid="admin-discount-note" value={form.note}
            onChange={(e) => set("note", e.target.value)} placeholder="Why (visible to you only)"
            className="h-11 border-ink-600 bg-ink-900 text-slate-100 placeholder:text-slate-600" />
          <Button data-testid="admin-discount-grant" disabled={busy || !Number(form.value)}
            onClick={() => act(() => api.post(`/admin/companies/${company.id}/discount`, {
              kind: form.kind, value: Number(form.value), duration: form.duration,
              months: Number(form.months) || 3, note: form.note,
            }), `Discount given to ${company.name}.`)}
            className="bg-emerald-500/20 text-emerald-100 hover:bg-emerald-500/30">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Give discount"}
          </Button>
        </div>
      )}
    </div>
  );
};
